import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/brand";
import BackToAdminLink from "@/components/BackToAdminLink";
import RichTextEditor from "@/components/RichTextEditor";
import { FloppyDisk, RocketLaunch, CalendarBlank, UploadSimple, X } from "@phosphor-icons/react";

const TYPES = [
  { value: "blog", label: "Blog post" },
  { value: "help", label: "Help article" },
];

// Must match backend HELP_CATEGORIES exactly. These are the sections on the
// marketing site's help hub.
const HELP_CATEGORIES = [
  { value: "getting-started", label: "Getting Started" },
  { value: "using-forecastly", label: "Using Forecastly" },
  { value: "understanding-your-numbers", label: "Understanding Your Numbers" },
  { value: "community-calls", label: "Community Calls" },
  { value: "account-billing", label: "Account & Billing" },
];

export default function AdminArticleEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const nav = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState({
    type: "blog",
    title: "",
    slug: "",
    excerpt: "",
    body_html: "",
    hero_image_url: "",
    seo_meta_description: "",
    tags: [],
    author_name: "",
    publish_at: "",
    category: "",
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("draft");
  const [publishedInfo, setPublishedInfo] = useState(null);
  const [tagsInput, setTagsInput] = useState("");
  const [blogCategoryOptions, setBlogCategoryOptions] = useState([]);
  const [uploadingHero, setUploadingHero] = useState(false);

  // Fetch existing blog categories once for the datalist suggestions.
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/articles/categories?type=blog`);
        setBlogCategoryOptions(res.data?.categories || []);
      } catch (_) {
        // Non-fatal: the input still works, users just won't see suggestions.
      }
    })();
  }, []);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/articles/${id}`);
        const a = res.data;
        setForm({
          type: a.type || "blog",
          title: a.title || "",
          slug: a.slug || "",
          excerpt: a.excerpt || "",
          body_html: a.body_html || "",
          hero_image_url: a.hero_image_url || "",
          seo_meta_description: a.seo_meta_description || "",
          tags: a.tags || [],
          author_name: a.author_name || "",
          publish_at: a.publish_at ? a.publish_at.slice(0, 16) : "",
          category: a.category || "",
        });
        setTagsInput((a.tags || []).join(", "));
        setStatus(a.status);
        setPublishedInfo(a.commit_sha ? { sha: a.commit_sha, url: `https://github.com/${(process.env.REACT_APP_MARKETING_REPO || "")}/commit/${a.commit_sha}` } : null);
      } catch (e) {
        toast.error("Failed to load article");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew]);

  const upd = (patch) => setForm((s) => ({ ...s, ...patch }));

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      tags: tagsInput.split(",").map((t) => t.trim()).filter(Boolean),
      publish_at: form.publish_at || null,
    };
    try {
      if (isNew) {
        const res = await axios.post(`${API}/admin/articles`, payload);
        toast.success("Draft saved");
        nav(`/admin/articles/${res.data.id}`, { replace: true });
      } else {
        await axios.patch(`${API}/admin/articles/${id}`, payload);
        toast.success("Saved");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (form.type === "help" && !form.category) {
      toast.error("Pick a Help Centre section before publishing this help article.");
      return;
    }
    if (!window.confirm(`Publish "${form.title}" now? This commits to your marketing repo and Cloudflare will rebuild the site in ~30-60 seconds.`)) return;
    await save();
    try {
      const res = await axios.post(`${API}/admin/articles/${id}/publish`);
      toast.success("Published! Cloudflare is rebuilding now.");
      setStatus("published");
      setPublishedInfo(res.data.commit ? {
        sha: res.data.commit.commit_sha,
        url: res.data.commit.url,
      } : null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    }
  };

  const schedule = async () => {
    if (!form.publish_at) {
      toast.error("Set a Publish date first");
      return;
    }
    await save();
    try {
      await axios.post(`${API}/admin/articles/${id}/schedule`, { publish_at: new Date(form.publish_at).toISOString() });
      toast.success("Scheduled — it'll publish itself when the date arrives.");
      setStatus("scheduled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Schedule failed");
    }
  };

  const unschedule = async () => {
    try {
      await axios.post(`${API}/admin/articles/${id}/unschedule`);
      toast.success("Back to draft");
      setStatus("draft");
    } catch (e) {
      toast.error("Failed");
    }
  };

  const uploadHero = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error("Only image files supported"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image over 5MB — please resize"); return; }
    setUploadingHero(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/articles/upload-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Relative URL — routed via ingress to backend, works in preview + prod.
      upd({ hero_image_url: res.data.preview_url });
      toast.success("Hero image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingHero(false);
    }
  };

  const pickHero = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e) => e.target.files?.[0] && uploadHero(e.target.files[0]);
    input.click();
  };

  if (user && !user.is_admin) return <Navigate to="/" replace />;
  if (loading) return <div className="p-8">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="admin-article-editor">
      {/* Header */}
      <div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <BackToAdminLink />
            <div className="section-eyebrow flex items-center gap-2">
              <Link to="/admin/articles" className="hover:underline">Admin · CMS · Articles</Link>
            </div>
            <h1 className="font-heading uppercase text-3xl mt-2">
              {isNew ? "New article" : form.title || "Untitled"}
            </h1>
            <div className="mt-2 text-xs">
              <span className="text-[10px] uppercase tracking-widest font-heading px-2 py-1 rounded border-[1.5px] border-black bg-white">
                {status}
              </span>
              {publishedInfo?.sha && (
                <a href={publishedInfo.url} target="_blank" rel="noreferrer" className="ml-2 text-black/60 hover:underline">
                  · commit {publishedInfo.sha.slice(0, 7)}
                </a>
              )}
            </div>
          </div>
          <div className="flex gap-2 shrink-0 mt-1 flex-wrap">
            <button
              type="button"
              className="btn-secondary"
              onClick={save}
              disabled={saving}
              data-testid="editor-save"
            >
              <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save draft"}
            </button>
            {status === "scheduled" ? (
              <button type="button" className="btn-secondary" onClick={unschedule}>
                Cancel schedule
              </button>
            ) : (
              <button
                type="button"
                className="btn-secondary"
                onClick={schedule}
                disabled={!id || !form.publish_at}
                title={!form.publish_at ? "Set a publish date first" : ""}
                data-testid="editor-schedule"
              >
                <CalendarBlank size={14} weight="bold" /> Schedule
              </button>
            )}
            <button
              type="button"
              className="btn-primary"
              onClick={publish}
              disabled={!id}
              data-testid="editor-publish"
            >
              <RocketLaunch size={14} weight="bold" /> Publish now
            </button>
          </div>
        </div>
      </div>

      {/* Meta */}
      <div className="card space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-black/60">Type</label>
            <select
              className="input mt-1"
              value={form.type}
              onChange={(e) => upd({ type: e.target.value })}
              data-testid="editor-type"
            >
              {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-black/60">Publish date (for scheduling or record)</label>
            <input
              type="datetime-local"
              className="input mt-1"
              value={form.publish_at}
              onChange={(e) => upd({ publish_at: e.target.value })}
              data-testid="editor-publish-at"
            />
          </div>
        </div>
        {form.type === "help" && (
          <div data-testid="editor-help-category-row">
            <label className="text-xs font-semibold uppercase tracking-widest text-black/60">
              Help Centre section
            </label>
            <select
              className="input mt-1"
              value={form.category}
              onChange={(e) => upd({ category: e.target.value })}
              data-testid="editor-help-category"
            >
              <option value="">— Pick a section —</option>
              {HELP_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <p className="text-xs text-black/50 mt-1">
              Where this article lives on the public Help Hub. Required before publishing.
            </p>
          </div>
        )}
        {form.type === "blog" && (
          <div data-testid="editor-blog-category-row">
            <label className="text-xs font-semibold uppercase tracking-widest text-black/60">
              Category
            </label>
            <input
              type="text"
              className="input mt-1"
              value={form.category}
              onChange={(e) => upd({ category: e.target.value })}
              list="blog-category-suggestions"
              placeholder="e.g. cash-flow, mindset, case-studies"
              data-testid="editor-blog-category"
            />
            <datalist id="blog-category-suggestions">
              {blogCategoryOptions.map((c) => <option key={c} value={c} />)}
            </datalist>
            <p className="text-xs text-black/50 mt-1">
              Pick an existing category or type a new one — it becomes a URL slug on the blog (spaces become hyphens automatically).
            </p>
          </div>
        )}
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-black/60">Title</label>
          <input
            type="text"
            className="input mt-1 !text-xl font-heading"
            value={form.title}
            onChange={(e) => upd({ title: e.target.value })}
            data-testid="editor-title"
            placeholder="Punchy, clear, benefit-led"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-black/60">
              Slug {isNew ? "(auto if blank)" : "(locked once published)"}
            </label>
            <input
              type="text"
              className="input mt-1 font-mono text-sm disabled:bg-black/5 disabled:text-black/60"
              value={form.slug}
              onChange={(e) => upd({ slug: e.target.value })}
              placeholder="my-article-slug"
              disabled={!isNew}
              title={isNew ? "" : "The slug is fixed at creation so re-publishing updates the same file in the marketing repo instead of creating a duplicate."}
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-black/60">Author name</label>
            <input
              type="text"
              className="input mt-1"
              value={form.author_name}
              onChange={(e) => upd({ author_name: e.target.value })}
              placeholder="forecastly"
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-black/60">Excerpt</label>
          <textarea
            rows={2}
            className="input mt-1"
            value={form.excerpt}
            onChange={(e) => upd({ excerpt: e.target.value })}
            placeholder="One-sentence hook. Written like a friend, not a marketer."
          />
          <p className="text-xs text-black/50 mt-1">
            Shown on the blog index next to each article card. Keep it to ~2 lines, benefit-led.
          </p>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-black/60">SEO meta description</label>
          <textarea
            rows={2}
            className="input mt-1"
            value={form.seo_meta_description}
            onChange={(e) => upd({ seo_meta_description: e.target.value.slice(0, 200) })}
            placeholder="Leave blank to reuse the excerpt."
          />
          <p className="text-xs text-black/50 mt-1">
            Shown on Google search results and social share previews (LinkedIn / Facebook / Slack). ~150–160 chars is ideal. Leave blank and we'll reuse the excerpt.
          </p>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-black/60">Hero image</label>
          <div className="mt-1 flex items-center gap-3 flex-wrap">
            {form.hero_image_url ? (
              <div className="relative">
                <img
                  src={form.hero_image_url}
                  alt="Hero"
                  className="h-24 w-40 object-cover rounded border-[1.5px] border-black"
                  data-testid="hero-preview"
                />
                <button
                  type="button"
                  onClick={() => upd({ hero_image_url: "" })}
                  className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border-[1.5px] border-black flex items-center justify-center hover:bg-red-100"
                  title="Remove hero image"
                  data-testid="hero-remove"
                >
                  <X size={12} weight="bold" />
                </button>
              </div>
            ) : (
              <div className="h-24 w-40 rounded border-[1.5px] border-dashed border-black/40 flex items-center justify-center text-xs text-black/40">
                No hero image
              </div>
            )}
            <button
              type="button"
              className="btn-secondary"
              onClick={pickHero}
              disabled={uploadingHero}
              data-testid="hero-upload"
            >
              <UploadSimple size={14} weight="bold" />
              {uploadingHero ? "Uploading…" : form.hero_image_url ? "Replace hero image" : "Upload hero image"}
            </button>
          </div>
          <p className="text-xs text-black/50 mt-1">
            Shows at the top of the article + on the blog index card. JPG/PNG/WebP, max 5MB. Gets committed to your marketing site's images folder automatically on publish.
          </p>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-black/60">Tags (comma-separated)</label>
          <input
            type="text"
            className="input mt-1"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            placeholder="cash-flow, solopreneur, forecast"
          />
        </div>
      </div>

      {/* Editor — WYSIWYG, no separate preview (the editor IS the preview) */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-black/60 mb-2">
          Article body
        </div>
        <RichTextEditor
          value={form.body_html}
          onChange={(html) => upd({ body_html: html })}
          placeholder="Write your article. Use the toolbar above."
          testId="cms-editor"
          minHeight={600}
        />
        <p className="text-xs text-black/50 mt-2">
          What you see here is what the blog post will look like. Bullets, spacing, headings, images and embeds render exactly as they'll publish.
        </p>
      </div>
    </div>
  );
}
